import {
  CLIENT_CONNECTIVITY_STATUS,
  getPerformedTaskNames
} from '@/state/signify'
import useStore from '@/state/store'
import { Spinner } from 'flowbite-react'
import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { Button } from '../base/Button'
import ModalComponent from '../base/Modal'
import Eye from '../icons/Eye'
import Toast from '../base/Toast'
import { FormattedMessage } from 'react-intl'
import { head } from 'lodash'
import { isValidPasscode } from '@/validations/auth'
import { emailService } from '@/utils/apis'
import { useRouter } from 'next/router'

const Footer = ({
  userPassCode,
  validationError
}: {
  userPassCode: string
  validationError: Dispatch<SetStateAction<boolean>>
}) => {
  const {
    clientConnectivityStatus,
    authentication: { userInfo },
    setForcedClose,
    connectToAgent,
    setClientConnectivityStatus
  } = useStore()
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    setSubmitting(
      clientConnectivityStatus === CLIENT_CONNECTIVITY_STATUS.CONNECTING
    )
  }, [clientConnectivityStatus])

  const checkPasscodeConnectWallet = async () => {
    if (!isValidPasscode(userPassCode)) {
      validationError(true)
      return
    }
    // userInfo + a primary email are required to look up the origin AID;
    // both are populated by the auth bootstrap before SignifyView mounts,
    // but strict null-checks doesn't propagate that — guard so a partial
    // session yields a clean validation error instead of a crash.
    const primaryEmail = head(userInfo?.emails)?.value
    if (!primaryEmail) {
      validationError(true)
      return
    }
    try {
      validationError(false)
      setClientConnectivityStatus(CLIENT_CONNECTIVITY_STATUS.CONNECTING)
      const originAid = await emailService.getUserOriginAidByEmail({
        email: primaryEmail
      })
      // getUserOriginAidByEmail can return a record without an `aid`
      // populated (unenrolled user); treat absence as an error rather than
      // passing undefined to connectToAgent.
      if (!originAid?.aid) {
        setClientConnectivityStatus(CLIENT_CONNECTIVITY_STATUS.ERROR)
        return
      }
      await connectToAgent(userPassCode, originAid.aid)
    } catch (ex) {
      setClientConnectivityStatus(CLIENT_CONNECTIVITY_STATUS.ERROR)
    }
    setForcedClose(false)
  }

  return (
    <div className="flex flex-row-reverse">
      <Button
        dataTestId="passcode-modal--confirm"
        label={
          <>
            <span className="flex items-center gap-2">
              {submitting && <Spinner color="info" aria-label="loading..." />}
              <>
                <FormattedMessage id="cta.confirm" />
              </>
            </span>
          </>
        }
        onClick={async () => await checkPasscodeConnectWallet()}
        disabled={userPassCode.length != 21 || submitting}
      />
    </div>
  )
}

const SignifyView = () => {
  const {
    promptForPassCode,
    clientConnectivityStatus,
    signifyTaskPerformed,
    resetAllStores,
    killSession,
    clearSignify
  } = useStore()

  const router = useRouter()

  const [showCode, setShowCode] = useState(false)
  const [userPassCode, setUserPassCode] = useState('')
  const [validationError, setValidationError] = useState(false)

  useEffect(() => {
    //console.log(
    //  '[SignifyView] promptForPassCode changed:',
    //  promptForPassCode,
    //  '| clientConnectivityStatus:',
    //  clientConnectivityStatus
    //)
  }, [promptForPassCode, clientConnectivityStatus])

  useEffect(() => {
    if (!promptForPassCode) return

    setUserPassCode('')
  }, [promptForPassCode])

  return (
    <>
      {clientConnectivityStatus === CLIENT_CONNECTIVITY_STATUS.ERROR &&
        promptForPassCode && (
          <Toast mode="ERROR">
            <FormattedMessage id="error.agentConnectionFailed" />
          </Toast>
        )}
      {validationError && promptForPassCode && (
        <Toast mode="ERROR">
          <FormattedMessage id="error.invalidPasscode" />
        </Toast>
      )}

      {signifyTaskPerformed.length > 0 && (
        <div
          data-testid="signify--task-performed-toast"
          // Test seam (see this.i d-8 (iii)): the raw operation keys that ran,
          // space-separated, so e2e can wait on a specific completed task via a
          // `~=` token selector instead of matching locale-dependent toast text.
          // Inert; MUST NOT drive any production behaviour.
          data-performed-tasks={Array.from(new Set(signifyTaskPerformed)).join(
            ' '
          )}
        >
          <Toast mode="INFO">
            <FormattedMessage id="common.label.performedTask" />{' '}
            {getPerformedTaskNames(signifyTaskPerformed).join(', ')}
          </Toast>
        </div>
      )}
      <ModalComponent
        show={promptForPassCode}
        dataTestId="passcode-modal"
        header={
          <>
            <FormattedMessage id="common.label.yourPassCode" />
            <p className="mt-2 text-base font-normal text-neutralsAdditional800">
              <FormattedMessage id="common.label.description" />
            </p>
          </>
        }
        onClose={() => {
          resetAllStores()
          killSession()
          clearSignify()
          router.push('/')
          // hidePassCodePrompt()
          // setForcedClose(true)
        }}
        size="lg"
        className="z-[999]"
        footer={
          <Footer
            userPassCode={userPassCode}
            validationError={setValidationError}
          />
        }
      >
        <div>
          <label htmlFor="confirmControlCode" className={`text-sm font-normal`}>
            <FormattedMessage id="page.generic.enterPasscode" />
          </label>
          <div
            className={`flex justify-between items-center border border-neutralsAdditional400 rounded-md px-3 py-[10px] mt-1`}
          >
            <input
              data-testid="passcode-modal--input"
              type={showCode ? 'text' : 'password'}
              placeholder="Enter passcode"
              className="w-full p-0 font-mono text-2xl border-none focus:ring-0 placeholder:font-sans placeholder:text-base"
              onChange={(e) => setUserPassCode(e.currentTarget.value)}
              value={userPassCode}
              maxLength={21}
            />
            <Eye
              className="cursor-pointer"
              show={!showCode}
              onClick={() => setShowCode(!showCode)}
            />
          </div>
        </div>
      </ModalComponent>
    </>
  )
}

export default SignifyView
